import type { TemplateDefinition } from "@/api/client";
import { clampPosition, clampSize, type Field, type Page, type SizePatch } from "@/features/templates/designer/geometry";

export type { Field };
/** A field edit; a key set to undefined is removed from the field. */
export type FieldPatch = { [K in keyof Field]?: Field[K] | undefined };
export type Watermark = { asset_key: string; opacity: number };

export const HISTORY_LIMIT = 100;

export type DesignerState = {
  past: TemplateDefinition[];
  present: TemplateDefinition;
  future: TemplateDefinition[];
  /** The definition last sent in a successful save; dirty means present is another object. */
  saved: TemplateDefinition;
  /** The coalesce key of the last edit, so a drag or typing run is one undo step. */
  coalesce: string | null;
};

type Coalesce = { coalesce?: string };
export type DesignerAction =
  | { type: "add"; field: Field }
  | ({ type: "move"; index: number; x_mm: number; y_mm: number } & Coalesce)
  | ({ type: "resize"; index: number } & SizePatch & Coalesce)
  | ({ type: "update"; index: number; patch: FieldPatch } & Coalesce)
  | { type: "remove"; index: number }
  | ({ type: "page"; patch: Partial<Page> } & Coalesce)
  | { type: "background"; asset_key: string | null }
  | ({ type: "watermark"; value: Watermark | null } & Coalesce)
  | { type: "undo" }
  | { type: "redo" }
  | { type: "saved"; definition: TemplateDefinition };

export const initDesigner = (definition: TemplateDefinition): DesignerState => ({
  past: [],
  present: definition,
  future: [],
  saved: definition,
  coalesce: null,
});

export const isDirty = (state: DesignerState) => state.present !== state.saved;
export const canUndo = (state: DesignerState) => state.past.length > 0;
export const canRedo = (state: DesignerState) => state.future.length > 0;

function withoutUndefined<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}

function patchField(def: TemplateDefinition, index: number, patch: FieldPatch): TemplateDefinition {
  const field = def.fields[index];
  if (!field) return def;
  const fields = def.fields.slice();
  fields[index] = withoutUndefined({ ...field, ...patch }) as Field;
  return { ...def, fields };
}

/** The definition an edit produces, before history bookkeeping. */
function edit(def: TemplateDefinition, action: DesignerAction): TemplateDefinition {
  switch (action.type) {
    case "add":
      return { ...def, fields: [...def.fields, action.field] };
    case "move": {
      const field = def.fields[action.index];
      return field ? patchField(def, action.index, clampPosition(field, action.x_mm, action.y_mm, def.page)) : def;
    }
    case "resize": {
      const field = def.fields[action.index];
      const { w_mm, h_mm, size_mm } = action;
      return field ? patchField(def, action.index, clampSize(field, { w_mm, h_mm, size_mm }, def.page)) : def;
    }
    case "update":
      return patchField(def, action.index, action.patch);
    case "remove":
      return action.index < def.fields.length ? { ...def, fields: def.fields.filter((_, i) => i !== action.index) } : def;
    case "page":
      return { ...def, page: { ...def.page, ...action.patch } };
    case "background": {
      const { background: _, ...rest } = def;
      return action.asset_key ? { ...rest, background: { asset_key: action.asset_key } } : rest;
    }
    case "watermark": {
      const { watermark: _, ...rest } = def;
      return action.value ? { ...rest, watermark: action.value } : rest;
    }
    default:
      return def;
  }
}

// Definitions are small, so comparing their JSON is a cheap and exact "did anything change".
const same = (a: TemplateDefinition, b: TemplateDefinition) => a === b || JSON.stringify(a) === JSON.stringify(b);

export function designerReducer(state: DesignerState, action: DesignerAction): DesignerState {
  switch (action.type) {
    case "undo": {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return { ...state, past: state.past.slice(0, -1), present: previous, future: [state.present, ...state.future], coalesce: null };
    }
    case "redo": {
      const [next, ...future] = state.future;
      if (!next) return state;
      return { ...state, past: [...state.past, state.present], present: next, future, coalesce: null };
    }
    case "saved":
      return { ...state, saved: action.definition, coalesce: null };
    default: {
      const next = edit(state.present, action);
      if (same(next, state.present)) return state;
      const key = "coalesce" in action ? (action.coalesce ?? null) : null;
      if (key !== null && key === state.coalesce) return { ...state, present: next, future: [] };
      const past = [...state.past, state.present].slice(-HISTORY_LIMIT);
      return { ...state, past, present: next, future: [], coalesce: key };
    }
  }
}
